import React, { FC } from 'react'
import {
  Popover,
  Paper,
  Typography,
  Button,
  Box,
  Chip,
  Divider
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import { ConfigSourceInfoProps, ConfigType, ConfigEntity } from '../../types/config'
import { getSourceDescription, getConfigTypeDisplayName } from '../../services/configApi'
import useAccount from '../../hooks/useAccount'

/**
 * ConfigSourceInfo - Popover showing where an inherited value comes from
 * 
 * Displays detailed information about the source of an inherited config value
 * and provides a button to edit the source configuration.
 */
const ConfigSourceInfo: FC<ConfigSourceInfoProps> = ({
  reference,
  anchorEl,
  open,
  onClose,
  onEditSource
}) => {
  const account = useAccount()
  const displayNames = {
    customerDisplayName: account.customer?.displayName,
    jobDisplayName: account.job?.displayName
  }

  if (!reference) {
    return null
  }

  const source = reference.source_entity
  const config = reference.source_config
  const typeDisplayName = getConfigTypeDisplayName(source.config_type || ConfigType.Global)

  // Create entity object for editing the source
  const createSourceEntity = (): ConfigEntity => {
    return {
      type: source.config_type || ConfigType.Global,
      customer: source.customer || '',
      job: source.job,
      folder_path: source.folder_path,
      table_name: source.table_name,
      display_name: getSourceDescription(reference, displayNames)
    }
  }

  const handleEditSource = () => {
    onEditSource(createSourceEntity())
    onClose()
  }

  // Get the field value from the source config
  const getFieldValue = (): string => {
    if (reference.value !== undefined && reference.value !== null) {
      if (typeof reference.value === 'string') {
        return reference.value
      }
      if (typeof reference.value === 'object') {
        try {
          return JSON.stringify(reference.value, null, 2)
        } catch (_err) {
          return String(reference.value)
        }
      }
      return String(reference.value)
    }

    const fieldName = reference.field_name as keyof typeof config.data
    const value = config.data[fieldName]

    // Handle array fields (like custom_personas)
    if (Array.isArray(value)) {
      return JSON.stringify(value, null, 2)
    }

    return (value as string) || 'Not set'
  }

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
    >
      <Paper sx={{ p: 2, maxWidth: 400, minWidth: 300 }}>
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Inherited Configuration
          </Typography>
          <Chip 
            label={typeDisplayName} 
            size="small" 
            color="primary" 
            variant="outlined"
            sx={{ mt: 0.5 }}
          />
        </Box>

        <Divider sx={{ my: 1.5 }} />

        <Box sx={{ mb: 1.5 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Source:
          </Typography>
          <Typography variant="body2" fontWeight="medium">
            {getSourceDescription(reference, displayNames)}
          </Typography>
        </Box>

        <Box sx={{ mb: 1.5 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Field: {reference.field_name}
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              fontStyle: 'italic',
              backgroundColor: 'grey.100',
              p: 1,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'grey.300'
            }}
          >
            "{getFieldValue()}"
          </Typography>
        </Box>

        {/* Show additional context based on config type */}
        {source.config_type === ConfigType.Folder && (
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              Folder Path: <code>{source.folder_path}</code>
            </Typography>
          </Box>
        )}

        {source.config_type === ConfigType.Table && (
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              Table: <code>{source.table_name}</code>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Location: <code>{source.folder_path}</code>
            </Typography>
          </Box>
        )}

        <Divider sx={{ my: 1.5 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Config ID: {config.id.substring(0, 8)}...
          </Typography>
          
          <Button
            variant="contained"
            size="small"
            startIcon={<EditIcon />}
            onClick={handleEditSource}
            sx={{ ml: 2 }}
          >
            Edit Source
          </Button>
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Click "Edit Source" to modify this configuration, which will affect all entities that inherit from it.
        </Typography>
      </Paper>
    </Popover>
  )
}

export default ConfigSourceInfo
